import { token } from '@atlaskit/tokens';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { fieldHasThreads } from '@databases/comments/utils';
import Z_INDEX from '@databases/layout/z-index';
import { type Database } from '@databases/types/database';
import {
  TABLE_CLASSNAMES,
  TABLE_VIEW_COLUMN_WIDTH_COMMENTS_BUFFER
} from '@databases/utils/constants';
import { type DatabaseDraft } from '@databases/utils/database/changesets';
import { legacyColors as colors } from '@databases/utils/legacy-colors';
import { produce } from 'immer';
import React, {
  type CSSProperties,
  type FunctionComponent,
  type ReactNode,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react';
import { createPortal } from 'react-dom';
import Draggable, { type DraggableEventHandler } from 'react-draggable';
import styled from 'styled-components';

import { LEFT_ACTIONS_MID_WIDTH as WIDTH_OFFSET } from './constants.ts';

/** Apply this class name to table cells that should be resizable. */
export const RESIZABLE = 'resizable';

/**
 * Dispatched on `document` during column drag-resize so that the
 * highlights system can redraw selection overlays in sync.
 *
 * @see {@link @databases/selections!subscribeToResize} (consumer)
 */
const COLUMN_RESIZE_EVENT = 'database-column-resize';

const THRESHOLD = 4;

/**
 * Returns the highest table cell ancestor of el that is still a descendant of container. If el does not have
 * any table cell ancestors that are a descendant of container, returns undefined.
 */
function getEventCell(
  el: Element | EventTarget,
  container: Element
): HTMLTableCellElement | undefined {
  const parent =
    el instanceof Element ? el.closest<HTMLTableCellElement>('th,td') : undefined;
  if (parent && container.contains(parent)) {
    return (
      (parent.parentElement && getEventCell(parent.parentElement, container)) || parent
    );
  }
}

/**
 * Returns the table cell whose resize handler the mouse is currently hovering, or undefined if there is none.
 * When hovering the left end of a cell, the left sibling cell should be resized. When hovering the right end
 * of a cell, the cell itself should be resized. If the cell to be resized does not have the RESIZABLE class
 * name, undefined is returned. When the user is hovering another part of a cell that the left or right end,
 * undefined is returned.
 */
function getResizeCell(
  e: React.MouseEvent,
  container: Element
): HTMLTableCellElement | undefined {
  const eventCell = getEventCell(e.target, container);
  if (eventCell) {
    let cell: HTMLTableCellElement | undefined = undefined;
    const dim = eventCell.getBoundingClientRect();
    if (Math.abs(e.clientX - dim.left) <= THRESHOLD) {
      // Resizing previous cell
      cell = eventCell.previousElementSibling as any;
    } else if (Math.abs(e.clientX - dim.right) <= THRESHOLD) {
      cell = eventCell;
    }

    if (cell && cell.classList.contains(RESIZABLE)) {
      return cell;
    }
  }
}

export function getResizeHandlerStyle(
  cell: HTMLTableCellElement,
  showEntryPrefix: boolean | undefined,
  widths?: Array<number | undefined>,
  container?: HTMLElement,
  isNativeEmbedEditorPlugin?: boolean
): CSSProperties {
  const cellIdx = getCellIdx(cell);

  const allColumns = Array.from(cell.parentElement?.children ?? []);

  const EntryPrefixHeader = allColumns.find((column) =>
    column.classList.contains('entry-number-table-header')
  );

  const left = allColumns
    .filter(isTableElement)
    .slice(0, cellIdx + 1)
    .reduce((p, c, i) => p + (widths?.[i] ?? c.offsetWidth), 0);

  const offsetWidthToSubtract =
    !showEntryPrefix && EntryPrefixHeader
      ? (EntryPrefixHeader as HTMLTableCellElement).offsetWidth
      : 0;
  let adjustedLeft = left - offsetWidthToSubtract;

  if (cell.dataset.dbsPinnedLeading === 'true' && container) {
    const cellRect = cell.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const renderedCellWidth = widths?.[cellIdx] ?? cell.offsetWidth;
    const resizeLineOffset = isNativeEmbedEditorPlugin ? 0 : WIDTH_OFFSET;
    const pinnedCellZIndex = Number.parseInt(cell.style.zIndex, 10);

    adjustedLeft =
      cellRect.left - containerRect.left + renderedCellWidth - resizeLineOffset;

    return {
      left: `${adjustedLeft}px`,
      ...(Number.isFinite(pinnedCellZIndex) ? { zIndex: pinnedCellZIndex + 1 } : {})
    };
  }

  return adjustedLeft !== 0 ? { left: `${adjustedLeft}px` } : { ...HIDDEN };
}

function getCellIdx(cell: HTMLTableCellElement): number {
  return Array.from(cell.parentElement?.children ?? [])
    .filter(isTableElement)
    .indexOf(cell);
}

export const IGNORED_TABLE_ELEMENT_CLASS = 'ignored-td-class';
export const IGNORED_TABLE_ELEMENT_WIDTH_CLASS = 'ignored-td-width-class';
const TABLE_ELEMENTS = ['th', 'td'];

export function isTableElement(child: Element): child is HTMLTableCellElement {
  return (
    TABLE_ELEMENTS.includes(child.localName) &&
    !child.className.includes(IGNORED_TABLE_ELEMENT_CLASS)
  );
}

export const TableResizerContainer = styled.div`
  position: relative;
`;

const ResizeHandler = styled.div.attrs({ 'data-interactive': true })<{
  isResizing: boolean;
  isCellSelected: boolean;
  isNativeEmbedEditorPlugin: boolean;
}>`
  position: absolute;
  top: ${token('space.100')};
  bottom: 0;
  width: 10px;
  margin-left: ${({ isNativeEmbedEditorPlugin }) =>
    isNativeEmbedEditorPlugin ? '0px' : token('space.050')};
  z-index: ${Z_INDEX.TABLE_RESIZE_HANDLER};
  cursor: col-resize;
  transform: none !important; // Override react-draggable position

  :after {
    content: ' ';
    position: absolute;
    height: 100%;
    width: 2px;
    left: ${({ isNativeEmbedEditorPlugin }) =>
      isNativeEmbedEditorPlugin ? '0px' : `${WIDTH_OFFSET - 4}px`};
    background-color: ${({ isCellSelected, isResizing }) => {
      if (isCellSelected) {
        return 'transparent';
      }
      return isResizing ? colors.B300 : colors.N30;
    }};
  }
`;

const ResizeCover = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: col-resize;
`;

const HIDDEN = { display: 'none' };

export interface TableResizerProps {
  /**
   * Should render a table, with the given column widths applied. The given column widths may differ from
   * the ones passed as the "widths" prop while resizing is in progress.
   */
  children: (widths: Array<number | undefined>) => ReactNode;

  /**
   * The widths of the columns in the table. Must be the same number of columns as there are in the table.
   * undefined means that there is no specific width set on a column, but the browser decides the width.
   */
  widths: Array<number | undefined>;

  /**
   * Called when a resize operation has finished. Note that multiple widths may change at once, since
   * the columns left of a resized column are converted to fixed width.
   */
  onResizeSubmit: (widths: Array<number | undefined>) => void;

  onResize?: (widths: Array<number | undefined>) => void;

  contentId: string;

  /**
   * Minimum width that a table column can be resized to.
   */
  minWidth?: number;

  isDisabled?: boolean;

  showEntryPrefix?: boolean;

  isNativeEmbedEditorPlugin?: boolean;

  fieldIds: string[];

  databaseDraft: Database | DatabaseDraft;
}

/**
 * Makes the columns of a table resizable. Each column can be resized by dragging its right border. When hovering
 * the right border, a blue column line is shown that can be dragged to resize the column. The resize operation
 * will only be available on cells that have the class name from the RESIZABLE constant applied. Nested tables
 * are ignored. Resizing is currently not supported in table rows where some cells have a colspan higher than 1.
 */
const TableResizer: FunctionComponent<TableResizerProps> = ({
  children,
  widths,
  onResizeSubmit,
  onResize,
  minWidth,
  isDisabled,
  contentId,
  showEntryPrefix,
  isNativeEmbedEditorPlugin,
  fieldIds,
  databaseDraft
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);

  const [cellRef, setCellRef] = useState<HTMLTableCellElement>();
  const [resizeData, setResizeData] = useState<
    | {
        cell: HTMLTableCellElement;
        cellIdx: number;
        hasMoved: boolean;
        /** New column widths, without min width applied */
        rawWidths: Array<number | undefined>;
        /** New column widths, with min width applied */
        effectiveWidths: Array<number | undefined>;
      }
    | undefined
  >(undefined);

  const client = useAnalytics();

  const resizeHandlerStyle = useMemo(() => {
    if (resizeData) {
      return getResizeHandlerStyle(
        resizeData.cell,
        showEntryPrefix,
        resizeData.effectiveWidths,
        containerRef.current ?? undefined,
        isNativeEmbedEditorPlugin
      );
    } else if (cellRef) {
      return getResizeHandlerStyle(
        cellRef,
        showEntryPrefix,
        undefined,
        containerRef.current ?? undefined,
        isNativeEmbedEditorPlugin
      );
    } else {
      return HIDDEN;
    }
  }, [cellRef, isNativeEmbedEditorPlugin, showEntryPrefix, resizeData]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (
        !resizeData &&
        containerRef.current &&
        !(
          resizeRef.current &&
          e.target instanceof Node &&
          resizeRef.current.contains(e.target)
        )
      ) {
        setCellRef(getResizeCell(e, containerRef.current));
      }
    },
    [resizeData]
  );

  const handleMouseLeave = useCallback(() => {
    setCellRef(undefined);
  }, []);

  const handleStart = useCallback<DraggableEventHandler>(() => {
    if (!cellRef) {
      return;
    }

    const cellIdx = getCellIdx(cellRef);
    const newWidths = [
      ...Array.from(cellRef.parentElement?.children ?? [])
        .filter(isTableElement)
        .slice(0, cellIdx + 1)
        .map((el) => el.offsetWidth),
      ...widths.slice(cellIdx + 1)
    ];
    setResizeData({
      hasMoved: false,
      cell: cellRef,
      cellIdx,
      rawWidths: newWidths,
      effectiveWidths: newWidths
    });
  }, [cellRef, widths]);

  const handleDrag = useCallback<DraggableEventHandler>(
    (e, data) => {
      if (data.deltaX === 0) {
        return;
      }

      if (!resizeData) {
        return;
      }

      const targetFieldId = fieldIds[resizeData.cellIdx - 1];
      const hasThreads = fieldHasThreads(databaseDraft, targetFieldId);

      const newResizeData = produce(resizeData, (resizeData) => {
        resizeData.hasMoved = true;
        const currentWidth = resizeData.rawWidths[resizeData.cellIdx];
        if (currentWidth !== undefined) {
          const newWidth = currentWidth + data.deltaX;
          resizeData.rawWidths[resizeData.cellIdx] = newWidth;
          resizeData.effectiveWidths[resizeData.cellIdx] = Math.max(
            newWidth,
            (minWidth ?? 0) + (hasThreads ? TABLE_VIEW_COLUMN_WIDTH_COMMENTS_BUFFER : 0)
          );
        }
      });
      const resizeWithoutNumberedEntriesColumn = newResizeData.effectiveWidths.slice(1);
      setResizeData(newResizeData);
      onResize?.(resizeWithoutNumberedEntriesColumn);
      document.dispatchEvent(new Event(COLUMN_RESIZE_EVENT));
    },
    [databaseDraft, fieldIds, minWidth, onResize, resizeData]
  );

  const handleStop = useCallback(() => {
    if (!resizeData) {
      return;
    }

    if (resizeData.hasMoved) {
      const resizeWithoutNumberedColumn = resizeData.effectiveWidths.slice(1);
      onResizeSubmit(resizeWithoutNumberedColumn);
      client.sendColumnResizedEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType()
      });
    } else {
      // Click on resize handler: reset width
      const newWidths = [...widths];
      newWidths[resizeData.cellIdx] = undefined;
      setCellRef(undefined);
      const newWidthsWithoutNumberedColumn = newWidths.slice(1);
      onResize?.(newWidthsWithoutNumberedColumn);
      onResizeSubmit(newWidthsWithoutNumberedColumn);
    }

    document.dispatchEvent(new Event(COLUMN_RESIZE_EVENT));
    setResizeData(undefined);
  }, [client, contentId, onResize, onResizeSubmit, resizeData, widths]);

  const isCellSelected = useMemo(() => {
    if (!cellRef) {
      return false;
    }
    return (
      cellRef.nextElementSibling?.classList.contains(TABLE_CLASSNAMES.IS_SELECTED) ||
      cellRef?.classList.contains(TABLE_CLASSNAMES.IS_SELECTED)
    );
  }, [cellRef]);

  return (
    <TableResizerContainer
      onMouseMove={isDisabled ? undefined : handleMouseMove}
      onMouseLeave={handleMouseLeave}
      ref={containerRef}
    >
      {children(resizeData?.effectiveWidths ?? widths)}
      <Draggable
        disabled={!cellRef}
        axis="x"
        onStart={handleStart}
        onDrag={handleDrag}
        onStop={handleStop}
      >
        <ResizeHandler
          style={resizeHandlerStyle}
          ref={resizeRef}
          isResizing={!!resizeData}
          isCellSelected={isCellSelected}
          isNativeEmbedEditorPlugin={!!isNativeEmbedEditorPlugin}
        />
      </Draggable>
      {resizeData && createPortal(<ResizeCover />, document.body)}
    </TableResizerContainer>
  );
};

export default TableResizer;
